import { BindingPath } from "../../Engine/src/Engine/Core/CoreTypes";
import { count, IterUtils, reverseArray } from "../IterableUtils";
import { ensureAndGetString, isNotNullOrUndefined, reject } from "../TypingUtils";

import { EachCurrentCollision } from "./EachCurrentCollision";
import { ModelPathBuilder } from "./ModelPathBuilder";
import { MatchKind, PathMatcher, PathMatching } from "./PathMatcher";
import { IEquatable } from "./IEquatable";

const EachSymbol = Symbol.for("__EACH__");
const CurrentSymbol = Symbol.for("__CURRENT__");

export class PathTokens {
    public static each: typeof EachSymbol = EachSymbol;
    public static current: typeof CurrentSymbol = CurrentSymbol;

    public static isMultiToken(part: undefined | PathToken): part is typeof EachSymbol | typeof CurrentSymbol {
        return part === PathTokens.each || part === PathTokens.current;
    }

    public static isSimpleToken(part: undefined | PathToken): part is string {
        return typeof part === "string";
    }

    public static isInstanceToken(part: undefined | PathToken): part is string {
        return typeof part === "string" && /^\d+$/.test(part);
    }

    public static isInstanceTokenOrInstancesSpec(part: undefined | PathToken): part is string {
        return typeof part === "string" && (/^\d+$/.test(part) || /^\[(\d+\,?)+\]$/i.test(part));
    }
}

export type SimplePathToken = string;

export type PathToken = SimplePathToken | typeof EachSymbol | typeof CurrentSymbol;

export function isModelPath(path: BindingPath | ModelPath | unknown): path is ModelPath {
    return typeof path !== "string" && path instanceof ModelPathImpl;
}

export function convertToLegacyPath(path: BindingPath | ModelPath): BindingPath {
    return isModelPath(path) ? path.toLegacyPath() : path;
}

export function convertToAbsoluteModelPath<R extends boolean, A extends boolean>(
    path: BindingPath | IModelPath<R, A>
): IModelPath<R, true>;
export function convertToAbsoluteModelPath(path: BindingPath | AbsoluteModelPath): AbsoluteModelPath {
    return isModelPath(path) ? path : createAbsoluteFromMask(path, PathTokens.each);
}

export function convertToRelativeModelPath<R extends boolean, A extends boolean>(
    path: BindingPath | IModelPath<R, A>
): IModelPath<R, false>;
export function convertToRelativeModelPath(path: BindingPath | RelativeModelPath): RelativeModelPath {
    return isModelPath(path) ? path : createRelativeFromMask(path, PathTokens.each);
}

export function emptyModelPath(): RelativeResolvedModelPath {
    return new ModelPathImpl<true, false>([], false);
}

export function emptyAbsoluteModelPath(): AbsoluteResolvedModelPath {
    return new ModelPathImpl<true, true>([], true);
}

export function createModelPath(path: string, absolute: true): AbsoluteResolvedModelPath;
export function createModelPath(path: string, absolute: false): RelativeResolvedModelPath;
export function createModelPath(path: string, absolute: "auto"): ResolvedModelPath;
export function createModelPath(path: string, absolute: boolean | "auto"): ResolvedModelPath {
    const partPartStrings = path.split("/");
    ensurePlainPathPartsDoesNotContainMultiplicity(partPartStrings);
    if (absolute === "auto") {
        if (path.startsWith("/")) {
            return new ModelPathImpl<true, true>(partPartStrings.slice(1), true);
        } else {
            return new ModelPathImpl<true, false>(partPartStrings, false);
        }
    }
    if (absolute && path.startsWith("/")) {
        return new ModelPathImpl<true, true>(partPartStrings.slice(1), true);
    }
    return new ModelPathImpl<true, boolean>(partPartStrings, absolute);
}

export function createFromMask(path: string, absolute: true, multiplicitySymbol: PathToken): AbsoluteModelPath;
export function createFromMask(path: string, absolute: false, multiplicitySymbol: PathToken): ResolvedModelPath;
export function createFromMask(path: string, absolute: "auto", multiplicitySymbol: PathToken): ModelPath;
export function createFromMask(path: string, absolute: boolean | "auto", multiplicitySymbol: PathToken): ModelPath {
    const partPartStrings = path.split("/").map(x => (x === "*" ? multiplicitySymbol : x));
    if (absolute === "auto") {
        if (path.startsWith("/")) {
            return new ModelPathImpl(partPartStrings.slice(1), true);
        } else {
            return new ModelPathImpl(partPartStrings, false);
        }
    }
    if (absolute && path.startsWith("/")) {
        return new ModelPathImpl(partPartStrings.slice(1), true);
    }
    return new ModelPathImpl(partPartStrings, absolute);
}

export function createAbsoluteFromMask(path: string, multiplicitySymbol: PathToken): AbsoluteModelPath {
    const partPartStrings = path.split("/").map(x => (x === "*" ? multiplicitySymbol : x));
    if (path.startsWith("/")) {
        return new ModelPathImpl(partPartStrings.slice(1), true);
    }
    return new ModelPathImpl(partPartStrings, true);
}

export function createRelativeFromMask(path: string, multiplicitySymbol: PathToken): RelativeModelPath {
    const partPartStrings = path.split("/").map(x => (x === "*" ? multiplicitySymbol : x));
    return new ModelPathImpl(partPartStrings, false);
}

function ensurePlainPathPartsDoesNotContainMultiplicity(parts: readonly string[]): void {
    if (parts.some(x => x === "*")) {
        throw new Error("Path must not contain * symbol");
    }
}

export function createAbsolute(path: string): AbsoluteResolvedModelPath {
    const partPartStrings = path.split("/");
    ensurePlainPathPartsDoesNotContainMultiplicity(partPartStrings);
    if (path.startsWith("/")) {
        return new ModelPathImpl<true, true>(partPartStrings.slice(1), true);
    }
    return new ModelPathImpl<true, true>(partPartStrings, true);
}

export function createRelativeResolvedModelPath(path: string): RelativeResolvedModelPath {
    const partPartStrings = path.split("/");
    ensurePlainPathPartsDoesNotContainMultiplicity(partPartStrings);
    return new ModelPathImpl<true, false>(partPartStrings, false);
}

export function createAbsoluteFromTokens(tokens: PathToken[]): AbsoluteModelPath {
    return new ModelPathImpl<false, true>(tokens, true);
}

export function createAbsoluteResolvedFromTokens(tokens: readonly string[]): AbsoluteResolvedModelPath {
    ensurePlainPathPartsDoesNotContainMultiplicity(tokens);
    return new ModelPathImpl<true, true>(tokens, true);
}

export function createRelativeFromTokens(tokens: readonly string[]): RelativeResolvedModelPath;
export function createRelativeFromTokens(tokens: readonly PathToken[]): RelativeModelPath;
export function createRelativeFromTokens(tokens: readonly PathToken[]): RelativeModelPath {
    return new ModelPathImpl<false, false>(tokens, false);
}

export function joinModelPaths(...parts: Array<ModelPath | PathToken | PathToken[]>): ModelPath {
    let absolute = false;
    let resultParts: PathToken[] = [];
    for (const joinItem of parts) {
        if (typeof joinItem === "string") {
            resultParts.push(joinItem);
        } else if (isModelPath(joinItem)) {
            if (joinItem.isAbsolute()) {
                absolute = true;
                resultParts = [...joinItem.getPathPartsAsArray()];
            } else {
                resultParts.push(...joinItem.getPathPartsAsArray());
            }
        } else if (Array.isArray(joinItem)) {
            resultParts.push(...joinItem);
        } else if (PathTokens.isMultiToken(joinItem)) {
            resultParts.push(joinItem);
        }
    }

    return new ModelPathImpl(resultParts, absolute);
}

export interface IModelPath<TResolved extends boolean, TAbsolute extends boolean>
    extends IEquatable<IModelPath<boolean, boolean>> {
    readonly resolved: TResolved;
    readonly absolute: TAbsolute;

    isEquals(modelPath: ModelPath, eachCurrentCollision?: EachCurrentCollision): boolean;
    getHash(eachCurrentCollision?: EachCurrentCollision): number;

    isResolved(): this is IModelPath<true, TAbsolute>;
    isAbsolute(): this is IModelPath<TResolved, true>;
    toAbsolute(): IModelPath<TResolved, true>;

    joinWith(...path: string[]): IModelPath<TResolved, TAbsolute>;
    joinWith(...path: PathToken[]): IModelPath<boolean, TAbsolute>;
    joinWith(...path: Array<ResolvedModelPath | string>): IModelPath<TResolved, TAbsolute>;
    joinWith(...path: Array<AbsoluteResolvedModelPath | string>): IModelPath<TResolved, true>;
    joinWith(...path: Array<ModelPath | PathToken>): IModelPath<boolean, TAbsolute>;

    getPathParts(): Iterable<PathTokenSpec<TResolved>>;
    getPathPartsAsArray(): ReadonlyArray<PathTokenSpec<TResolved>>;
    getSimpleTokensStrict(): Iterable<SimplePathToken>;
    getSimpleTokensAsArrayStrict(): readonly SimplePathToken[];

    resolveAll(instance: string): ModelPath;
    isContainIteration(): boolean;
    isContainCurrentIteration(): boolean;
    isContainCurrentOrEachIteration(): boolean;
    getLastListPath(): PathToken[];
    getPathWithoutLastIteration(): IModelPath<TResolved, TAbsolute>;
    getPathWithoutIterations(): IModelPath<TResolved, TAbsolute>;
    getLastIndexOfCurrentOrEachIteration(): number | -1;
    isEndsWithIteration(): boolean;
    isEndsWithCurrentIteration(): boolean;
    isIncludes(modelPath: ModelPath): boolean;
    getIterationRank(eachCurrentCollision?: EachCurrentCollision): number;
    isEmpty(): boolean;
    matchFor(modelPath: ModelPath): PathMatching;
    startsWith(modelPath: ModelPath): boolean;
    endsWith(modelPath: ModelPath): boolean;
    splitByMultiplicity(): Iterable<ResolvedModelPath | typeof EachSymbol | typeof CurrentSymbol>;
    normalize(): IModelPath<TResolved, TAbsolute>;
    toCurrentIteration(): IModelPath<TResolved, TAbsolute>;
    toEachIteration(): IModelPath<TResolved, TAbsolute>;
    getAllParentPaths(): Iterable<ModelPath>;
    trimLastStarIfLastToken(): IModelPath<TResolved, TAbsolute>;
    trimFirstStarIfFirstToken(): IModelPath<TResolved, TAbsolute>;
    getSuffixPathFrom(modelFieldPath: ModelPath): RelativeModelPath;
    getParentPath(): IModelPath<TResolved, TAbsolute>;
    applyInstancesDirty(
        instances: SimplePathToken[],
        fillWithMultiplicityIfHasNoInstance?: boolean
    ): IModelPath<TResolved, TAbsolute>;
    applyCommonInstancesFromAnotherPath(modelPath: ModelPath): IModelPath<TResolved, TAbsolute>;
    getFirstMultiplicityInstanceNumber(): number;
    tokensCount(): number;
    toString(): string;
    toLegacyPath(): string;
}

export type ModelPath = IModelPath<boolean, boolean>;
export type ResolvedModelPath = IModelPath<true, boolean>;
export type AbsoluteModelPath = IModelPath<boolean, true>;
export type AbsoluteResolvedModelPath = IModelPath<true, true>;

export type RelativeModelPath = IModelPath<boolean, false>;
export type RelativeResolvedModelPath = IModelPath<true, false>;

type PathTokenSpec<TResolved extends boolean> = TResolved extends true ? string : PathToken;

type PathTokensSpec<TResolved extends boolean> = Array<PathTokenSpec<TResolved>>;

type ReadonlyPathTokensSpec<TResolved extends boolean> = ReadonlyArray<PathTokenSpec<TResolved>>;

function isAllTokensResolved<TResolved extends boolean>(tokens: ReadonlyPathTokensSpec<TResolved>): TResolved {
    // Я что-то немогу сообразить как тут всё строго написать...
    // @ts-expect-error
    return tokens.every(PathTokens.isSimpleToken);
}

export class ModelPathImpl<TResolved extends boolean, TAbsolute extends boolean>
    implements IEquatable<ModelPath>, IModelPath<TResolved, TAbsolute>
{
    private readonly tokens: ReadonlyPathTokensSpec<TResolved>;
    public readonly absolute: TAbsolute;
    private legacyPath: undefined | string;
    private cachedHash: number | undefined;

    public constructor(pathParts: ReadonlyPathTokensSpec<TResolved>, absolute: TAbsolute) {
        this.tokens = pathParts.filter(x => x !== "");
        this.absolute = absolute;
    }

    public get resolved(): TResolved {
        return isAllTokensResolved(this.tokens);
    }

    public getPathParts(): Iterable<PathTokenSpec<TResolved>> {
        return this.tokens;
    }

    public getPathPartsAsArray(): ReadonlyArray<PathTokenSpec<TResolved>> {
        return this.tokens;
    }

    public *getSimpleTokensStrict(): Iterable<SimplePathToken> {
        for (const token of this.tokens) {
            if (!PathTokens.isSimpleToken(token)) {
                throw new Error("Path must not contain spec tokens");
            }
            yield token;
        }
    }

    public getSimpleTokensAsArrayStrict(): readonly SimplePathToken[] {
        for (const token of this.tokens) {
            if (!PathTokens.isSimpleToken(token)) {
                throw new Error("Path must not contain spec tokens");
            }
        }
        // Проверка выше гараентирует что будут только SimplePathToken.
        // Все map и filter убраны для производительности
        // @ts-ignore
        return this.tokens;
    }

    public joinWith(...path: string[]): IModelPath<TResolved, TAbsolute>;
    public joinWith(...path: PathToken[]): IModelPath<boolean, TAbsolute>;
    public joinWith(...path: Array<ResolvedModelPath | string>): IModelPath<TResolved, TAbsolute>;
    public joinWith(...path: Array<AbsoluteResolvedModelPath | string>): IModelPath<TResolved, true>;
    public joinWith(...path: Array<ModelPath | PathToken>): IModelPath<boolean, TAbsolute>;
    public joinWith(...path: Array<ModelPath | PathToken>): IModelPath<boolean, boolean> {
        return joinModelPaths(this, ...path);
    }

    public getFirstMultiplicityInstanceNumber(): number {
        const firstIndex = this.tokens.find(PathTokens.isInstanceToken);
        return firstIndex ? Number(firstIndex) : -1;
    }

    public tokensCount(): number {
        return this.tokens.length;
    }

    public isResolved(): this is IModelPath<true, TAbsolute> {
        return isAllTokensResolved(this.tokens);
    }

    public resolveAll(instance: string): ModelPath {
        const tokens = this.tokens.map(token => {
            if (PathTokens.isSimpleToken(token)) {
                return token;
            } else {
                return instance;
            }
        });

        return new ModelPathImpl(tokens, this.absolute);
    }

    public isContainIteration(): boolean {
        return this.tokens.some(x => x === PathTokens.each);
    }

    public isContainCurrentIteration(): boolean {
        return this.tokens.some(x => x === PathTokens.current);
    }

    public isContainCurrentOrEachIteration(): boolean {
        return this.tokens.some(x => x === PathTokens.current || x === PathTokens.each);
    }

    public getLastListPath(): PathToken[] {
        if (!this.isContainIteration()) {
            throw new Error("Path must contain iteration");
        }

        // Проверка выше гарантирует, что будет PathTokens.each
        // @ts-ignore
        const lastIterationIndex = this.tokens.lastIndexOf(PathTokens.each);
        return this.tokens.slice(0, lastIterationIndex);
    }

    public getPathWithoutLastIteration(): IModelPath<TResolved, TAbsolute> {
        if (!this.isContainCurrentOrEachIteration()) {
            return this;
        }

        const lastIterationIndex = this.getLastIndexOfCurrentOrEachIteration();
        if (lastIterationIndex === -1) {
            throw new Error("Path must contain current or each iteration");
        }

        const tokensWithoutLastIteration = this.tokens.slice(0, lastIterationIndex);

        return new ModelPathImpl(tokensWithoutLastIteration, this.absolute);
    }

    public getPathWithoutIterations(): IModelPath<TResolved, TAbsolute> {
        if (!this.isContainCurrentOrEachIteration()) {
            return this;
        }
        const tokens = this.tokens.filter(x => !PathTokens.isMultiToken(x));
        return new ModelPathImpl(tokens, this.absolute);
    }

    public getLastIndexOfCurrentOrEachIteration(): number | -1 {
        if (!this.isContainCurrentOrEachIteration()) {
            return -1;
        }

        // Проверка выше гарантирует, что будет PathTokens.each или PathTokens.current
        // @ts-ignore
        const lastIterationIndex = this.tokens.lastIndexOf(PathTokens.each);
        // @ts-ignore
        const lastCurrentIterationIndex = this.tokens.lastIndexOf(PathTokens.current);

        return Math.max(lastIterationIndex, lastCurrentIterationIndex);
    }

    public isEndsWithIteration(): boolean {
        const lastItem = IterUtils.last(this.tokens);
        return lastItem == PathTokens.each;
    }

    public isEndsWithCurrentIteration(): boolean {
        const lastItem = IterUtils.last(this.tokens);
        return lastItem == PathTokens.current;
    }

    public isAbsolute(): this is IModelPath<TResolved, true> {
        return this.absolute;
    }

    public toString(): string {
        if (this.tokens.length === 0) {
            return "";
        }
        return (this.absolute ? "/" : "") + this.tokens.map(x => this.pathPartToString(x)).join("/");
    }

    public toLegacyPath(): string {
        return this.legacyPath ?? (this.legacyPath = this.tokens.map(x => this.pathPartToString(x)).join("/"));
    }

    public isEquals(
        modelPath: ModelPath,
        eachCurrentCollision: EachCurrentCollision = EachCurrentCollision.AreSame
    ): boolean {
        if (eachCurrentCollision === EachCurrentCollision.AreSame) {
            return this.toLegacyPath() === modelPath.toLegacyPath();
        } else {
            return (
                this.isAbsolute() === modelPath.isAbsolute() &&
                Iterator.from(IterUtils.zip(this.getPathParts(), modelPath.getPathParts())).every(
                    ([t1, t2]) => t1 === t2
                )
            );
        }
    }

    /**
     * Всегда целое положительное беззнаковое 32-битное число
     */
    public getHash(eachCurrentCollision: EachCurrentCollision = EachCurrentCollision.AreSame): number {
        if (this.cachedHash == undefined) {
            let hash = 5381;
            for (const token of reverseArray(this.tokens)) {
                let currentStr: string;
                if (PathTokens.isSimpleToken(token)) {
                    currentStr = token;
                } else {
                    if (eachCurrentCollision === EachCurrentCollision.AreDifferent) {
                        currentStr = token === PathTokens.each ? "*" : "!";
                    } else {
                        currentStr = "*";
                    }
                }
                let stringIndex = currentStr.length;
                while (stringIndex) {
                    hash = (hash * 33) ^ currentStr.charCodeAt(--stringIndex);
                }
            }

            /*
                JavaScript does bitwise operations (like XOR, above) on 32-bit signed
                integers. Since we want the results to be always positive, convert the
                signed int to an unsigned by doing an unsigned bitshift.
          */
            this.cachedHash = hash >>> 0;
        }
        return this.cachedHash;
    }

    public isIncludes(modelPath: ModelPath): boolean {
        const targetTokens = modelPath.getPathPartsAsArray();
        if (this.tokens.length !== targetTokens.length) {
            return false;
        }
        for (let i = 0; i < this.tokens.length; i++) {
            const leftPart = this.tokens[i];
            const rightPart = targetTokens[i];
            if (typeof leftPart === "string") {
                if (typeof rightPart === "string") {
                    if (leftPart !== rightPart) {
                        return false;
                    }
                    continue;
                } else {
                    return false;
                }
            } else if (leftPart === PathTokens.current || leftPart === PathTokens.each) {
                if (typeof rightPart === "string") {
                    if (!isFinite(Number(rightPart))) {
                        return false;
                    }
                    continue;
                } else {
                    if (rightPart !== PathTokens.current && rightPart !== PathTokens.each) {
                        return false;
                    }
                    continue;
                }
            }
        }
        return true;
    }

    public getIterationRank(eachCurrentCollision: EachCurrentCollision = EachCurrentCollision.AreDifferent): number {
        return count(
            Iterator.from(this.tokens).filter(x =>
                eachCurrentCollision == EachCurrentCollision.AreDifferent
                    ? x === PathTokens.each
                    : PathTokens.isMultiToken(x)
            )
        );
    }

    public isEmpty(): boolean {
        return this.tokens.length === 0;
    }

    public matchFor(modelPath: ModelPath): PathMatching {
        if (!this.isAbsolute()) {
            throw new Error(`Cannot match non absolute path '${this.toString()}'`);
        }
        if (!modelPath.isAbsolute()) {
            throw new Error(`Cannot match non absolute path '${modelPath.toString()}'`);
        }
        return PathMatcher.match(this.tokens, modelPath.getPathPartsAsArray());
    }

    public startsWith(modelPath: ModelPath): boolean {
        const thisParts = this.getPathPartsAsArray();
        const otherParts = modelPath.getPathPartsAsArray();
        return otherParts.every((value, index) => value === thisParts[index]);
    }

    public endsWith(modelPath: ModelPath): boolean {
        const thisParts = this.getPathPartsAsArray().slice().reverse();
        const otherParts = modelPath.getPathPartsAsArray().slice().reverse();
        return otherParts.every((value, index) => value === thisParts[index]);
    }

    public *splitByMultiplicity(): Iterable<ResolvedModelPath | typeof EachSymbol | typeof CurrentSymbol> {
        let lastSimpleParts = new ModelPathBuilder(this.isAbsolute());
        for (const token of this.tokens) {
            if (PathTokens.isSimpleToken(token)) {
                lastSimpleParts.append(token);
            } else {
                if (!lastSimpleParts.empty) {
                    yield lastSimpleParts.toResolvedPath();
                    lastSimpleParts = new ModelPathBuilder(false);
                }
                yield token;
            }
        }
        if (!lastSimpleParts.empty) {
            yield lastSimpleParts.toResolvedPath();
        }
    }

    public normalize(): IModelPath<TResolved, TAbsolute> {
        const resultTokens: PathTokensSpec<TResolved> = [];
        for (const pathPart of this.tokens) {
            if (pathPart === "..") {
                if (!resultTokens.pop()) {
                    if (this.isAbsolute()) {
                        throw new Error(`Path is invalid '${this.toString()}'`);
                    } else {
                        resultTokens.push("..");
                    }
                }
                continue;
            }

            if (pathPart === ".") {
                continue;
            }
            resultTokens.push(pathPart);
        }
        return new ModelPathImpl<TResolved, TAbsolute>(resultTokens, this.absolute);
    }

    /**
     * @summary Спецификаторы множественности Each заменяет на Current
     */
    public toCurrentIteration(): IModelPath<TResolved, TAbsolute> {
        if (this.isResolved()) {
            return this;
        } else {
            // NOTE Тут не получается наложить дискриминатор
            // @ts-expect-error
            const pathParts: PathTokensSpec<TResolved> = this.tokens.map(x =>
                x === PathTokens.each ? PathTokens.current : x
            );
            return new ModelPathImpl<TResolved, TAbsolute>(pathParts, this.absolute);
        }
    }

    public toEachIteration(): IModelPath<TResolved, TAbsolute> {
        if (this.isResolved()) {
            return this;
        } else {
            // NOTE Тут не получается наложить дискриминатор
            // @ts-expect-error
            const pathParts: PathTokensSpec<TResolved> = this.tokens.map(x =>
                x === PathTokens.current ? PathTokens.each : x
            );
            return new ModelPathImpl<TResolved, TAbsolute>(pathParts, this.absolute);
        }
    }

    public toAbsolute(): IModelPath<TResolved, true> {
        if (!this.absolute) {
            throw new Error(`Path '${this.toLegacyPath()} is not absolute'`);
        }
        return new ModelPathImpl<TResolved, true>(this.tokens, true);
    }

    public *getAllParentPaths(): Iterable<ModelPath> {
        const result = new ModelPathBuilder(this.isAbsolute());
        for (const token of IterUtils.skipLast(this.getPathParts(), 1)) {
            result.append(token);
            yield result.toPath();
        }
    }

    public trimLastStarIfLastToken(): IModelPath<TResolved, TAbsolute> {
        const lastItem = IterUtils.last(this.tokens);
        if (lastItem != undefined && PathTokens.isMultiToken(lastItem)) {
            return new ModelPathImpl(this.tokens.slice(0, -1), this.absolute);
        }
        return this;
    }

    public trimFirstStarIfFirstToken(): IModelPath<TResolved, TAbsolute> {
        const firstItem = IterUtils.first(this.tokens);
        if (firstItem != undefined && PathTokens.isMultiToken(firstItem)) {
            return new ModelPathImpl(this.tokens.slice(1), this.absolute);
        }
        return this;
    }

    public getSuffixPathFrom(modelFieldPath: ModelPath): RelativeModelPath {
        return createRelativeFromTokens(
            modelFieldPath
                .matchFor(this)
                .map(x => (x.kind === MatchKind.DifferentSuffix ? x.source : undefined))
                .filter(isNotNullOrUndefined)
                .flatMap(x => x.getPathPartsAsArray())
        );
    }

    public getParentPath(): IModelPath<TResolved, TAbsolute> {
        const result = new ModelPathBuilder(this.absolute);
        for (const token of IterUtils.skipLast(this.getPathParts(), 1)) {
            result.append(token);
        }
        // Тут получается так, что по другому никак
        // @ts-expect-error
        return result.toPath();
    }

    public applyInstancesDirty(
        instances: SimplePathToken[],
        fillWithMultiplicityIfHasNoInstance?: boolean
    ): IModelPath<TResolved, TAbsolute> {
        if (this.isResolved() || instances.length === 0) {
            return this;
        }
        let instanceCounter = 0;
        const result = new ModelPathBuilder(true);
        for (const token of this.getPathParts()) {
            if (PathTokens.isMultiToken(token) && instanceCounter < instances.length) {
                const instanceOrEach =
                    instances[instanceCounter] ??
                    (fillWithMultiplicityIfHasNoInstance == true ? PathTokens.each : reject());
                result.append(instanceOrEach);
                instanceCounter++;
            } else {
                result.append(token);
            }
        }
        // Тут получается так, что по другому никак
        // @ts-expect-error
        return result.toPath();
    }

    public applyCommonInstancesFromAnotherPath(modelPath: ModelPath): IModelPath<TResolved, TAbsolute> {
        const anotherTokens = modelPath.getPathPartsAsArray();
        const updatedTokens = this.tokens.reduce(
            (acc, token, index) => {
                const anotherToken = anotherTokens[index];
                const nextTokenInfo =
                    acc.matching &&
                    ((PathTokens.isMultiToken(token) && PathTokens.isInstanceToken(anotherToken)) ||
                        (PathTokens.isSimpleToken(token) &&
                            PathTokens.isSimpleToken(anotherToken) &&
                            token === anotherToken))
                        ? { nextToken: anotherToken, matching: true }
                        : { nextToken: token, matching: false };
                acc.matching = nextTokenInfo.matching;
                acc.tokens[index] = nextTokenInfo.nextToken;
                return acc;
            },
            {
                tokens: Array.from(this.tokens),
                matching: true,
            }
        ).tokens;
        return new ModelPathImpl<TResolved, TAbsolute>(updatedTokens, this.absolute);
    }

    private pathPartToString(pathPart: PathToken): string {
        if (typeof pathPart === "string") {
            return pathPart;
        }
        if (pathPart === PathTokens.each) {
            return "*";
        }
        if (pathPart === PathTokens.current) {
            return "*";
        }
        throw new Error(`Unknown path part specified: ${ensureAndGetString(pathPart)}`);
    }
}
